import { Box, Grid, MenuItem, TextField } from "@mui/material";
import {
  DatePicker,
  DateValidationError,
  PickersActionBarAction,
} from "@mui/x-date-pickers";
import React from "react";
import { ScormExportFileDeliveryStatus } from "services/KnowfullyAdminClient";
import persistanceStore, { PersistanceStore } from "stores/persistanceStore";
import { standardDateFormat } from "utils/dateUtils";
import { STATUS_MESSAGE_IDS, Translate } from "./types";

const DATE_ERROR_MESSAGE_IDS: Partial<Record<string, string>> = {
  invalidDate: "exportedScormFilesDateInvalid",
  minDate: "exportedScormFilesDateAfterEnd",
  maxDate: "exportedScormFilesDateBeforeStart",
};

/**
 * ⛔ The formatter is a parameter, not useIntl inside here: helperText is a plain MUI string prop with no
 * FormattedMessage in the path, so an unresolved id is rendered verbatim - the operator would read
 * "exportedScormFilesDateInvalid" under the field, and the field would explain nothing while looking as
 * though it did.
 */
const errorMessage = (error: DateValidationError, t: Translate) => {
  const id = error ? DATE_ERROR_MESSAGE_IDS[error] : undefined;

  return id ? t({ id }) : undefined;
};

/**
 * The one place both date filters agree on how they look, so a change lands once. Outlined at the small
 * size is what the Tag and Status filters beside them use: the standard variant reserves space above the
 * input for its label, which leaves the field sitting lower than they do however the row is aligned.
 */
const filterDateSlotProps = (error: DateValidationError, t: Translate) => ({
  textField: {
    fullWidth: true,
    variant: "outlined" as const,
    size: "small" as const,
    InputLabelProps: { shrink: true },
    error: !!error,
    helperText: errorMessage(error, t),
  },
  actionBar: { actions: ["clear", "cancel"] as PickersActionBarAction[] },
});

type ExportedScormFilesFiltersProps = {
  t: Translate;
  tagOptions: string[];
  tag?: string;
  status?: ScormExportFileDeliveryStatus;
  startDate?: Date;
  endDate?: Date;
  startDateError: DateValidationError;
  endDateError: DateValidationError;
  onStartDateChange: (
    date: Date | null,
    context: { validationError: DateValidationError }
  ) => void;
  onEndDateChange: (
    date: Date | null,
    context: { validationError: DateValidationError }
  ) => void;
  onStartDateError: (reason: DateValidationError) => void;
  onEndDateError: (reason: DateValidationError) => void;
};

/**
 * Tag, Status and the export date range. The Organization filter is deliberately absent - LEG-3625 excludes
 * it, and Organization is a column here rather than a control.
 *
 * The two selections write straight to the persistance store because the filter outlives navigation inside
 * the screen; the dates are handed up instead, because normalising them before they reach the store is the
 * caller's rule.
 */
export const ExportedScormFilesFilters = ({
  t,
  tagOptions,
  tag,
  status,
  startDate,
  endDate,
  startDateError,
  endDateError,
  onStartDateChange,
  onEndDateChange,
  onStartDateError,
  onEndDateError,
}: ExportedScormFilesFiltersProps) => (
  <Box mb={3}>
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          select
          fullWidth
          variant="outlined"
          size="small"
          label={t({ id: "addExportCoursesTag" })}
          value={tag ?? ""}
          // Without displayEmpty the Select renders nothing for the empty "All" option.
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          onChange={(e) =>
            persistanceStore.update((s: PersistanceStore) => {
              s.exportedScormFilesTag = e.target.value || undefined;
            })
          }
        >
          <MenuItem value="">
            {t({ id: "allLabel" })}
          </MenuItem>
          {tagOptions.map((tagOption) => (
            <MenuItem key={tagOption} value={tagOption}>
              {tagOption}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          select
          fullWidth
          variant="outlined"
          size="small"
          label={t({ id: "statusLabel" })}
          value={status ?? ""}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          onChange={(e) =>
            persistanceStore.update((s: PersistanceStore) => {
              s.exportedScormFilesStatus =
                (e.target.value as ScormExportFileDeliveryStatus) || undefined;
            })
          }
        >
          <MenuItem value="">
            {t({ id: "allLabel" })}
          </MenuItem>
          {Object.values(ScormExportFileDeliveryStatus).map((statusOption) => (
            <MenuItem key={statusOption} value={statusOption}>
              {t({ id: STATUS_MESSAGE_IDS[statusOption] })}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <DatePicker
          label={t({ id: "startDateLabel" })}
          value={startDate ?? null}
          format={standardDateFormat}
          maxDate={endDate}
          onChange={(date, context) => onStartDateChange(date, context)}
          onError={(reason) => onStartDateError(reason)}
          slotProps={filterDateSlotProps(startDateError, t)}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <DatePicker
          label={t({ id: "endDateLabel" })}
          value={endDate ?? null}
          format={standardDateFormat}
          minDate={startDate}
          onChange={(date, context) => onEndDateChange(date, context)}
          onError={(reason) => onEndDateError(reason)}
          slotProps={filterDateSlotProps(endDateError, t)}
        />
      </Grid>
    </Grid>
  </Box>
);
